import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { apiErrorText } from '../../utils/apiErrorText';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Preview, edit and send a polite payment reminder for one overdue sales invoice. */
export const PaymentReminderDialog = ({ invoice, onClose, onSent }) => {
  const { t } = useTranslation();
  const [lang, setLang] = useState(null);
  const [form, setForm] = useState(null);
  const [attach, setAttach] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const q = lang ? `?lang=${lang}` : '';
    axios.get(`${API}/invoices/${invoice.id}/reminder-preview${q}`)
      .then((r) => { setForm((f) => ({ ...r.data, to: f?.to ?? r.data.to, cc: f?.cc ?? '' })); if (!lang) setLang(r.data.lang); })
      .catch((e) => { toast.error(apiErrorText(e, t, 'reminder.failed')); onClose(); });
  }, [invoice.id, lang]);

  const send = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/invoices/${invoice.id}/send-reminder`, {
        to: form.to, cc: form.cc || null, subject: form.subject, body: form.body, lang, attach_pdf: attach,
      });
      toast.success(t('reminder.sent', { to: form.to }));
      onSent();
    } catch (e) { toast.error(apiErrorText(e, t, 'reminder.failed')); }
    setBusy(false);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="reminder-dialog">
        <DialogHeader><DialogTitle>{t('reminder.title', { number: invoice.number })}</DialogTitle></DialogHeader>
        {!form ? <p className="text-sm text-slate-500">…</p> : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
              <span>{t('reminder.language')}:</span>
              {['pl', 'en'].map((l) => (
                <Button key={l} size="sm" variant={lang === l ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => setLang(l)} data-testid={`reminder-lang-${l}`}>
                  {l === 'pl' ? 'Polski' : 'English'}{form.auto_lang === l ? ` · ${t('reminder.auto')}` : ''}
                </Button>
              ))}
              {form.reminder_count > 0 && <span data-testid="reminder-previous">{t('reminder.previous', { count: form.reminder_count, date: String(form.last_reminder_at).slice(0, 10) })}</span>}
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <div><Label className="text-xs">{t('reminder.to')}</Label><Input value={form.to} onChange={set('to')} data-testid="reminder-to" /></div>
              <div><Label className="text-xs">{t('reminder.cc')}</Label><Input value={form.cc} onChange={set('cc')} data-testid="reminder-cc" /></div>
            </div>
            {!form.to && <p className="text-xs text-amber-700" data-testid="reminder-no-email">{t('reminder.noEmail')}</p>}
            <div><Label className="text-xs">{t('reminder.subject')}</Label><Input value={form.subject} onChange={set('subject')} data-testid="reminder-subject" /></div>
            <div><Label className="text-xs">{t('reminder.body')}</Label><Textarea rows={14} value={form.body} onChange={set('body')} className="font-mono text-xs" data-testid="reminder-body" /></div>
            {!form.bank?.iban && <p className="text-xs text-amber-700" data-testid="reminder-no-bank">{t('reminder.noBank', { currency: form.currency })}</p>}
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={attach} onCheckedChange={(v) => setAttach(!!v)} data-testid="reminder-attach" />{t('reminder.attach')}</label>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="reminder-cancel">{t('common.cancel')}</Button>
          <Button disabled={!form || busy || !form.to || !form.subject || !form.body} onClick={send} data-testid="reminder-send">
            <Send className="h-4 w-4 mr-1" />{busy ? t('reminder.sending') : t('reminder.send')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentReminderDialog;
